import React, { useCallback, useEffect, useState } from 'react';
import { useFormContext, useFormState, useWatch } from 'react-hook-form';
import { Button } from '@ontruck/orc';
import cx from 'classnames';
import _ from 'lodash';

import { useVenueDetail } from './VenueDetailContext';

const VenueDetail = ({ loading, title, children, fieldName, value }) => {
  const [defaultValues, setDefaultValues] = useState({});
  const { getValues, setValue } = useFormContext();
  const { isSubmitting, touchedFields, isSubmitted, errors } = useFormState();
  const { onEditClick, onDiscardClick, editMode, isCreating } = useVenueDetail();
  const { [fieldName]: editedValue } = useWatch(fieldName);

  const isTouched = !!(touchedFields || {})[fieldName] && isSubmitted && !isCreating;

  const isEditing = !!editMode[fieldName] || isCreating;
  const error = errors[fieldName];

  const handleEditClick = useCallback(
    (e) => {
      e.preventDefault();
      onEditClick(fieldName);
    },
    [fieldName, onEditClick]
  );

  const handleDiscardClick = useCallback(
    (e) => {
      e.preventDefault();
      setValue(fieldName, defaultValues[fieldName]);
      onDiscardClick(fieldName);
    },
    [fieldName, onDiscardClick, defaultValues, setValue]
  );

  const valueForChildren = editedValue || value;

  const childrenWithProps = React.Children.map(children, (child) => {
    if (React.isValidElement(child)) {
      return React.cloneElement(child, {
        isEditing,
        isCreating,
        fieldName,
        error,
        value: valueForChildren,
      });
    }
    return child;
  });

  useEffect(() => {
    if (!isEditing) {
      return;
    }

    const defaultValue = getValues(fieldName);

    setDefaultValues({ [fieldName]: _.cloneDeep(defaultValue) });
  }, [isEditing, fieldName, getValues]);

  if (!children) {
    return null;
  }

  return (
    <div
      className={cx('venue-detail', {
        loading: loading,
        isTouched: isTouched,
      })}
      data-testid="VenueDetail"
    >
      {!isEditing && !!fieldName && (
        <div className="venue-edit-container">
          <Button text="Edit" nude onClick={handleEditClick} disabled={isSubmitting} />
        </div>
      )}
      <div className="venue-title">{title}</div>
      <div className="venue-description">
        <>{childrenWithProps}</>
      </div>
      {!isCreating && isEditing && (
        <div className="venue-save-container">
          <Button
            nude
            text="Discard changes"
            onClick={handleDiscardClick}
            disabled={isSubmitting}
          />
          <Button text="Save changes" type="submit" disabled={isSubmitting} />
        </div>
      )}
    </div>
  );
};

const defaultDetail = '-';

export default VenueDetail;
export { defaultDetail };
